const STORAGE_KEY = 'safeguardpro_external_messages_v1';

const readMessages = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeMessages = (messages) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
};

const normalizeRecipients = (recipients = []) =>
  recipients.map((recipient) => ({
    id: recipient.id,
    name: recipient.name || 'Unknown',
    type: recipient.type || 'student'
  }));

const normalizeMessage = (message) => ({
  id: message.id || `extmsg-${Math.random().toString(36).slice(2, 10)}`,
  subject: message.subject || 'Message',
  body: message.body || '',
  category: message.category || 'update',
  fromType: message.fromType || 'staff',
  fromId: message.fromId,
  fromName: message.fromName || 'Unknown',
  recipients: normalizeRecipients(message.recipients || []),
  staffRecipients: message.staffRecipients || [],
  createdAt: message.createdAt || new Date().toISOString(),
  readBy: message.readBy || [],
  starredBy: message.starredBy || [],
  deletedBy: message.deletedBy || [],
  attachments: message.attachments || [],
  references: message.references || []
});

export default class ExternalMessageService {
  list() {
    return readMessages().map((message) => normalizeMessage(message));
  }

  listForStaff(staffId) {
    return this.list().filter((message) => {
      if (message.deletedBy?.includes(staffId)) return false;
      return message.fromId === staffId || (message.staffRecipients || []).includes(staffId);
    });
  }

  listForStudent(studentId) {
    return this.list().filter((message) => {
      if (message.deletedBy?.includes(studentId)) return false;
      if (message.fromType === 'student' && message.fromId === studentId) return true;
      return (message.recipients || []).some((recipient) => recipient.type === 'student' && recipient.id === studentId);
    });
  }

  listInboxForStaff(staffId) {
    return this.listForStaff(staffId).filter(
      (message) => message.fromType === 'student' && (message.staffRecipients || []).includes(staffId)
    );
  }

  listSentForStaff(staffId) {
    return this.listForStaff(staffId).filter(
      (message) => message.fromType === 'staff' && message.fromId === staffId
    );
  }

  sendFromStaff({ subject, body, fromUser, recipients = [], attachments = [], references = [], category = 'update' }) {
    const message = normalizeMessage({
      subject,
      body,
      category,
      fromType: 'staff',
      fromId: fromUser.id,
      fromName: fromUser.name,
      recipients,
      staffRecipients: [fromUser.id],
      readBy: [],
      starredBy: [],
      deletedBy: [],
      attachments,
      references
    });
    const next = [message, ...readMessages()];
    writeMessages(next);
    return message;
  }

  logStudentMessage({ subject, body, student, staffRecipients = [], attachments = [], references = [], category = 'update' }) {
    const message = normalizeMessage({
      subject,
      body,
      category,
      fromType: 'student',
      fromId: student.id,
      fromName: student.name,
      recipients: staffRecipients.map((staff) => ({ id: staff.id, name: staff.name, type: 'staff' })),
      staffRecipients: staffRecipients.map((staff) => staff.id),
      readBy: [],
      starredBy: [],
      deletedBy: [],
      attachments,
      references
    });
    const next = [message, ...readMessages()];
    writeMessages(next);
    return message;
  }

  markRead(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      if (message.readBy.includes(userId)) return message;
      return { ...message, readBy: [...message.readBy, userId] };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  toggleStar(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      const isStarred = message.starredBy.includes(userId);
      const starredBy = isStarred
        ? message.starredBy.filter((entry) => entry !== userId)
        : [...message.starredBy, userId];
      return { ...message, starredBy };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  deleteForUser(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      if (message.deletedBy.includes(userId)) return message;
      return { ...message, deletedBy: [...message.deletedBy, userId] };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }
}
